import { createRef, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { initialValues } from '../../formik'
import { ENTITY_TYPES } from '@sales-center/common'
import { useData, useGetOpportunityTitle } from '../../hooks'
import type { IAddOpportunityFormModel } from '../../typings'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'

import { useAppDispatch } from '@user/store/hooks'
import {
  locationToBillToLocationDTOAddOpp,
  locationToServiceLocationDTOAddOpp,
} from '@user/store/entityLocations'
import {
  selectAddOpportunitySlice,
  setAddOppBillTo,
  setAddOppLocations,
} from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useNoteCodes } from '@user/components/Opportunities/AddOpportunityPage/AddOpportunityForm/hooks/useNoteCodes'
import { useSelectCustomFieldData } from '@user/store/customFields'
import { customValuesToFormModel } from '@user/components/CustomFields'

export function useDuplicateData() {
  const loading = useData()
  const dispatch = useAppDispatch()
  const ref = createRef<HTMLDivElement>()

  const { state } = useLocation()

  const title = useGetOpportunityTitle(state?.billTo)
  const { billTo, serviceLocations } = useSelector(selectAddOpportunitySlice)

  const { noteCodes } = useNoteCodes()

  useEffect(() => {
    if (state?.billTo) {
      dispatch(setAddOppBillTo(locationToBillToLocationDTOAddOpp(state.billTo)))
    }
    if (state?.serviceLocations)
      dispatch(
        setAddOppLocations(
          state?.serviceLocations.map((s) =>
            locationToServiceLocationDTOAddOpp(s),
          ),
        ),
      )
  }, [state?.billTo, dispatch, state?.serviceLocations])

  const { defaultFunnel, firstStage } = useGetSalesFunnelData(
    ENTITY_TYPES.Opportunity,
  )
  const externalSvcLocId = state?.serviceLocations[0].id ?? ''

  const { customFields } = useSelectCustomFieldData()

  const hydratedInitialValues = useMemo<IAddOpportunityFormModel>(
    () => ({
      ...initialValues,
      serviceLocations,
      customFields: customValuesToFormModel(customFields),
      billToExternalLinkId: billTo?.externalLinkId ?? '',
      salesFunnelId: defaultFunnel?.id || '',
      opportunityStageId: firstStage?.id.toString() || '',
      externalLocationId: externalSvcLocId,
      addNoteToPestPac: Boolean(externalSvcLocId),
      serviceLocation:
        serviceLocations.length === 1 ? serviceLocations[0].id.toString() : '',
      noteCode: externalSvcLocId
        ? noteCodes.find((code) => code.isDefault)?.name
        : '',
    }),
    [
      billTo?.externalLinkId,
      serviceLocations,
      externalSvcLocId,
      defaultFunnel?.id,
      firstStage?.id,
      noteCodes,
      customFields,
    ],
  )

  const initialStateValid = !!state
  const hasRequiredData =
    initialStateValid &&
    !!state?.billTo &&
    !!state?.serviceLocations &&
    state?.serviceLocations.length > 0
  return {
    ref,
    billTo,
    loading,
    title,
    serviceLocations,
    externalSvcLocId,
    initialStateValid,
    initialValues: hydratedInitialValues,
    hasRequiredData,
    noteCodes,
  }
}
